import { useCalendarSettings } from "@repo/api";
import { LightMode } from "@repo/skapa";
import { Variables } from "@repo/skapa";
import { t } from "@repo/translation";
import { useTranslation } from "@repo/translation";
import { DATE_FORMAT } from "@repo/ui";
import { type ReactNode, useRef } from "react";
import ReactDatePicker from "react-datepicker";

import { DatePickerInput } from "./DatePicker";
import { DatePickerStyleContainer } from "./InlineDatePicker";

interface SingleDatePickerProps {
  onChange: (date: Date) => void;
  value: Date | undefined;
  id: string;
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  customInput?: ReactNode;
  allowClear?: boolean;
  onClear?: (date: undefined) => void;
}

export const SingleDatePicker = ({
  onChange,
  onClear,
  value,
  id,
  label,
  minDate,
  maxDate,
  customInput,
}: SingleDatePickerProps) => {
  const { dateFnsLocale } = useTranslation();
  const ref = useRef<ReactDatePicker>(null);
  const { data: calendarSettings } = useCalendarSettings();

  return (
    <DatePickerStyleContainer
      border={`${Variables.thicknessThin} solid ${LightMode.colourNeutral3}`}
    >
      <ReactDatePicker
        id={`toDate-${id}`}
        readOnly={false}
        ref={ref}
        customInput={
          (customInput ?? <DatePickerInput label={label ?? "Date"} />) as any
        }
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            ref?.current?.setOpen(false);
          }
        }}
        onChange={(d: Date | null) => {
          if (d) {
            onChange(d);
          } else if (onClear) {
            onClear?.(undefined);
          }
        }}
        locale={dateFnsLocale}
        dateFormat={DATE_FORMAT}
        minDate={minDate}
        maxDate={maxDate}
        selected={value}
        todayButton={t("label.today")}
        showWeekNumbers={calendarSettings.showWeekNumber}
        calendarStartDay={calendarSettings.firstDayOfWeek === "sunday" ? 0 : 1}
        isClearable={onClear !== undefined}
      />
    </DatePickerStyleContainer>
  );
};
